/**
 * Every piece of text for the /career page redesign
 * (src/components/career/CareerPage.js). Components only render this — no
 * page copy is hardcoded in the component itself.
 *
 * The open-roles list itself is NOT here — that's live ERP data, fetched
 * in src/pages/career.js exactly as it was before this redesign (see that
 * file and src/components/CareerSecond.js, which this redesign restyles
 * but does not refetch or reshape).
 */

const LIFE_AT_VISIOMATE_ROUTE = "/lifeAtVisiomate"
const TALENT_EMAIL = "talent@visiomate.com"

const HERO = {
  eyebrow: "CAREERS AT VISIOMATE",
  // Plain text — no highlight on any word (the design spec this redesign now
  // matches exactly explicitly removes the earlier yellow highlight on
  // "grows").
  heading: "Build a career with a team that grows with you.",
  body: "Work on real projects for clients in the US, UK, Europe and the Middle East, and keep learning as you go.",
  primaryCta: { label: "See open roles", href: "#roles" },
  secondaryCta: { label: "Life at Visiomate", to: LIFE_AT_VISIOMATE_ROUTE },
  // "two colleagues working at a desk" — found on a second pass through
  // src/images/lifeatvisiomate (not under a "desk"-named file, which is
  // why it was missed before): good_environement1.webp, a real Visiomate
  // photo of the team at their desks in front of the office's blue/yellow
  // wall. Spans both rows on the left. "Navy Visiomate polo shirt and
  // bottle" reuses the same photo already used for this on
  // /life-at-visiomate (it also shows a navy polo and a bottle, alongside
  // a white one), now the top-right tile.
  photoDeskFile: "lifeatvisiomate/good_environement1.webp",
  photoDeskAlt: "Colleagues working together at their desks at the Visiomate office",
  photoFile: "lifeatvisiomate/visiomate_branding.jpg",
  photoAlt: "A navy Visiomate polo shirt and a branded bottle",
  tileHeading: "Roles open now",
  tileSub: "See them below",
}

const WORD_BAND = ["LEARNING", "FLEXIBLE WORK", "REWARDS", "EQUAL OPPORTUNITY"]

const WHY_JOIN = {
  eyebrow: "WHY JOIN US",
  heading: "Four things you can count on.",
  // icon: reuses CareerFirst.js's existing icon set (book/balance/gift/
  // heart) rather than introducing a new one — see CareerPage.js.
  tiles: [
    {
      key: "a",
      icon: "book",
      label: "CONTINUOUS LEARNING",
      text: "Grow through international projects, structured learning circles and sponsored training and certifications.",
      theme: "deep-navy",
      span: 7,
    },
    {
      key: "b",
      icon: "balance",
      label: "WORK-LIFE BALANCE",
      text: "Remote and flexible work arrangements, family-friendly policies, generous time off and on-site activities.",
      theme: "near-black",
      span: 5,
    },
    {
      key: "c",
      icon: "gift",
      label: "REWARDS & PERKS",
      text: "Long-stay awards, bi-annual performance reviews, annual increments and a competitive perks package.",
      theme: "slate",
      span: 5,
    },
    {
      key: "d",
      icon: "heart",
      label: "INCLUSIVE CULTURE",
      text: "A merit-first place where every background is respected and opportunity is open to all, equally.",
      theme: "yellow",
      span: 7,
    },
  ],
}

const ROLES_SECTION = {
  eyebrow: "OPEN ROLES",
  heading: "Available opportunities.",
  intro: "Pick a role to read the details and apply. Each application goes straight to our hiring team.",
  cvCard: {
    title: "Do not see your role?",
    body: "Send us your CV anyway. We read every one.",
    cta: { label: "Send your CV", href: `mailto:${TALENT_EMAIL}` },
  },
}

const NEXT_STEPS = {
  eyebrow: "AFTER YOU APPLY",
  heading: "What happens next.",
  steps: [
    { num: 1, title: "We read your application", text: "Our hiring team reads every application and replies, whatever the answer.", theme: "blue" },
    { num: 2, title: "A first conversation", text: "A short call to get to know you and tell you more about the role.", theme: "near-black" },
    { num: 3, title: "Show your skills", text: "A talk about your work, or a short task that fits the role.", theme: "slate" },
    { num: 4, title: "Offer and first week", text: "If it is a fit, we make an offer and plan your first week together.", theme: "yellow" },
  ],
}

const FAQ = {
  eyebrow: "QUESTIONS",
  heading: "Before you apply.",
  items: [
    {
      q: "Do you hire fresh graduates and interns?",
      a: `Yes. We work with universities to give young talent real-world experience. Apply to an open role, or send your CV to ${TALENT_EMAIL}.`,
      theme: "deep-navy",
    },
    {
      q: "Can I work remotely?",
      a: "Yes. We offer remote and flexible work arrangements, along with generous time off and family-friendly policies.",
      theme: "near-black",
    },
    {
      q: "How long does hiring take?",
      a: "Hiring usually takes two to three weeks.",
      theme: "yellow",
    },
    {
      q: "What technologies will I work with?",
      a: "On web and mobile projects: React.js, Next.js, Node.js, TypeScript, .NET Core and Python, with PostgreSQL, GraphQL, AWS, Docker and Kubernetes. On business systems: Odoo and Microsoft Dynamics 365. Your role decides which of these you use.",
      theme: "slate",
    },
  ],
}

const LIFE_BANNER = {
  left: "Curious what the days are like?",
  right: "See life at Visiomate",
  to: LIFE_AT_VISIOMATE_ROUTE,
}

const CLOSING = {
  heading: "Still have a question?",
  body: `Write to our hiring team at ${TALENT_EMAIL}. A real person replies.`,
  cta: { label: "Email the hiring team", href: `mailto:${TALENT_EMAIL}` },
}

module.exports = {
  LIFE_AT_VISIOMATE_ROUTE,
  TALENT_EMAIL,
  HERO,
  WORD_BAND,
  WHY_JOIN,
  ROLES_SECTION,
  NEXT_STEPS,
  FAQ,
  LIFE_BANNER,
  CLOSING,
}
